// 공식 JavaScript API 기반 HCELL Service
import type { HcellSample, HcellSamplesConfig } from '../types/hcell';
import type { HcellExecuteScriptMessage } from '@/shared/types/sdkMessages';

import * as yaml from 'js-yaml';

import { getLogger } from '@/lib/logger/logger';
import { hcellSamplesCache } from '@/lib/storage/idb';
import { SdkIframeService } from '@/shared/services/SdkIframeService';
import { isMessageType } from '@/shared/types/sdkMessages';

import i18n from '../../../i18n';
import { HCELL_ENGINE_ORIGIN } from '../constants';

import { HcellScriptGenerator } from './hcellScriptGenerator';

const logger = getLogger('HcellService');

// ───────────────────────────────────────────────────────────
// ① 전역 핸들러: 톱레벨 실행 금지, 지연 init
// ───────────────────────────────────────────────────────────
let _errorHandlerInitialized = false;

// ✅ P0-3: Store handlers for cleanup
const unhandledRejectionHandler = (event: Event) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const reason = (event as PromiseRejectionEvent).reason;
  if (typeof reason === 'string' && reason.includes('sendErrorToLogServer')) {
    event.preventDefault();
    logger.warn('HCELL 엔진 관련 Promise rejection 무시됨:', reason);
  }
};

const errorHandler = (event: Event) => {
  const message = (event as ErrorEvent).message || '';
  if (
    message.includes('sendErrorToLogServer') ||
    message.includes('Cannot read properties of undefined') ||
    message.includes('Load Count Fail')
  ) {
    event.preventDefault();
    logger.warn('HCELL 엔진 내부 오류 무시됨:', message);
  }
};

function setupHcellErrorHandlerOnce() {
  if (_errorHandlerInitialized) return;
  _errorHandlerInitialized = true;

  // logger.error를 덮어쓰되, 원본은 캡처
  const originalError = logger.error.bind(logger);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  (logger as any).error = (message: string, error?: unknown) => {
    const msg = typeof message === 'string' ? message : String(message);
    if (
      msg.includes('sendErrorToLogServer') ||
      msg.includes('Cannot read properties of undefined') ||
      msg.includes('Load Count Fail')
    ) {
      return; // HCELL 엔진 내부 오류는 무시
    }
    originalError(message, error);
  };

  window.addEventListener('unhandledrejection', unhandledRejectionHandler);
  window.addEventListener('error', errorHandler);
}

// ✅ P0-3: Cleanup function for error handlers
export function cleanupHcellErrorHandlers() {
  window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
  window.removeEventListener('error', errorHandler);
}

// ───────────────────────────────────────────────────────────
// ② hcellSamples 캐시: 지연 초기화 패턴 + IndexedDB 지원
// ───────────────────────────────────────────────────────────
// ✅ Cache version for YAML integration
const CACHE_VERSION = 3; // Increment when cache structure changes (v3: Fixed insertImage sdk.WriteFile)

// ✅ P1-1: Use proper types instead of 'any'
let hcellSamplesMemoryCache: HcellSamplesConfig | null = null;
const lastLoadedLanguageCache = new Map<string, HcellSamplesConfig>();

async function loadHcellSamples(lang: string): Promise<HcellSamplesConfig> {
  // ✅ P0-2: Try IndexedDB cache first with version check
  try {
    const cached = await hcellSamplesCache.get(lang) as (HcellSamplesConfig & { _cacheVersion?: number }) | null;
    if (cached?._cacheVersion === CACHE_VERSION) {
      logger.info(`[HCell] Loaded samples from IndexedDB cache v${CACHE_VERSION}: ${lang}`);
      return cached;
    } else if (cached) {
      logger.info(`[HCell] Cache version mismatch (cached: ${cached._cacheVersion}, current: ${CACHE_VERSION}), reloading...`);
    }
  } catch (e) {
    logger.warn('[HCell] IndexedDB cache read failed:', e);
  }

  // Fetch samples.json from network
  const jsonRes = await fetch(`/samples/hcell-sdk/${lang}/samples.json`);
  const jsonData = (await jsonRes.json()) as HcellSamplesConfig;

  // Fetch code-samples.yaml from network
  const yamlRes = await fetch(`/samples/hcell-sdk/${lang}/code-samples.yaml`);
  const yamlText = await yamlRes.text();
  const yamlData = yaml.load(yamlText) as Record<string, { javascript: string }>;

  // Merge YAML data into JSON data
  const mergedData: HcellSamplesConfig = {
    SDK_EXAMPLES: {}
  };

  // Copy all samples from JSON and add javascript code from YAML
  Object.keys(jsonData.SDK_EXAMPLES || {}).forEach((featureId) => {
    const jsonSample = jsonData.SDK_EXAMPLES[featureId];
    mergedData.SDK_EXAMPLES[featureId] = {
      ...jsonSample,
      javascript: yamlData[featureId]?.javascript
    } as HcellSample;
  });

  logger.info(`[HCell] Merged ${Object.keys(mergedData.SDK_EXAMPLES).length} samples from JSON and YAML`);

  // ✅ P0-2: Save to IndexedDB cache with version
  const cachedData = {
    ...mergedData,
    _cacheVersion: CACHE_VERSION
  };

  try {
    await hcellSamplesCache.set(lang, cachedData);
    logger.info(`[HCell] Saved samples to IndexedDB cache v${CACHE_VERSION}: ${lang}`);
  } catch (e) {
    logger.warn('[HCell] IndexedDB cache write failed:', e);
  }

  return mergedData;
}

// ───────────────────────────────────────────────────────────
// ③ 서비스 클래스: 생성자에서는 "순서 보장"만, 무거운 작업은 별도 init
// ───────────────────────────────────────────────────────────
// ✅ P1-1: Type-safe service (interface provides documentation, not strict implementation)
export class HcellService extends SdkIframeService {
  readonly sdkName = 'HcellService';
  readonly defaultTimeoutMs = 15000;
  override get i18nNamespace(): string {
    return 'hcell';
  }

  private _initialized = false; // 재초기화 방지
  // ✅ P0-3: Store SDK ready listener for cleanup
  private sdkReadyListener: ((event: MessageEvent) => void) | null = null;

  constructor() {
    super();
    // 전역 등록만 (가벼운 작업)
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      (window as any).hcellService = this;
    }
    // SDK READY 리스너는 가볍고 안전
    this.setupSDKReadyListener();
  }

  // 앱 부트스트랩에서 명시적으로 호출하도록 공개 init 제공
  public async init() {
    if (this._initialized) return;
    this._initialized = true;

    // 전역 오류 처리기: 여기서 1회 등록
    setupHcellErrorHandlerOnce();

    // i18n 준비 후 샘플 로딩
    // URL path와 localStorage를 우선 확인해 초기 언어 결정 (region 감지 전에도 정확한 언어 사용)
    let lang: string = i18n?.language || 'ko';
    const path = window.location.pathname;
    if (path.startsWith('/ja')) {
      lang = 'ja';
    } else {
      const savedLang = localStorage.getItem('i18nextLng');
      if (savedLang === 'en' || savedLang === 'ja') {
        lang = savedLang;
      }
    }
    try {
      logger.info(`Loading HCELL samples for language: ${lang}`);
      const result = await loadHcellSamples(lang);
      lastLoadedLanguageCache.set(lang, result);
      // 로드하는 동안 언어가 바뀌었으면 memory cache 업데이트 안 함
      // (waitForCurrentLanguage()가 올바른 언어로 덮어쓸 것)
      const currentLang = i18n?.language || 'ko';
      if (currentLang === lang) {
        hcellSamplesMemoryCache = result;
        logger.info('HCELL samples loaded successfully');
      } else {
        logger.info(`[HCell] Language changed during init (${lang} → ${currentLang}), skipping memory cache update`);
      }
    } catch (e) {
      logger.warn('hcellSamples cache initialization failed:', e);
      // 폴백 없음 — waitForCurrentLanguage()가 올바른 언어로 재시도
    }

    // 언어 변경 시에만 다시 로드(리스너 중복 방지용 once)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
    i18n?.off?.('languageChanged', this._onLangChange as any);
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    i18n?.on?.('languageChanged', this._onLangChange);
  }

  private readonly _onLangChange = async () => {
    try {
      const lang = i18n?.language || 'ko';

      // ✅ P0-2: Check if we need to reload (language-aware caching)
      const needsReload = !hcellSamplesMemoryCache ||
                          hcellSamplesMemoryCache !== lastLoadedLanguageCache.get(lang);

      if (!needsReload) {
        logger.info(`[HCell] Using cached samples for language: ${lang}`);
        return;
      }

      logger.info(`Reloading HCELL samples for language: ${lang}`);
      hcellSamplesMemoryCache = await loadHcellSamples(lang);
      lastLoadedLanguageCache.set(lang, hcellSamplesMemoryCache);
    } catch (e) {
      logger.warn('Reload hcellSamples failed:', e);
    }
  };

  // region 감지 완료 후 올바른 언어의 samples 로드를 보장
  public async waitForCurrentLanguage(): Promise<void> {
    const lang = i18n?.language || 'ko';
    // 이미 해당 언어로 로드됨 → 즉시 반환
    if (lastLoadedLanguageCache.get(lang) === hcellSamplesMemoryCache && hcellSamplesMemoryCache) {
      return;
    }
    // 새 언어로 로드
    try {
      logger.info(`[HCell] waitForCurrentLanguage: loading samples for language: ${lang}`);
      hcellSamplesMemoryCache = await loadHcellSamples(lang);
      lastLoadedLanguageCache.set(lang, hcellSamplesMemoryCache);
    } catch (e) {
      logger.warn('[HCell] waitForCurrentLanguage failed:', e);
    }
  }

  // SDK 초기화 완료 리스너 설정
  private setupSDKReadyListener(): void {
    // ✅ P0-3: Store listener reference for cleanup
    this.sdkReadyListener = (event: MessageEvent) => {
      // HCELL 엔진에서 오는 메시지만 처리
      if (
        event.origin === HCELL_ENGINE_ORIGIN ||
        isMessageType(event.data, 'SDK_READY')
      ) {
        // SDK 준비 신호 감지 (postMessage만 사용)
        if (isMessageType(event.data, 'SDK_READY')) {
          logger.info('HcellService: SDK initialization completed');
          this.setReady();
        }
      }
    };
    window.addEventListener('message', this.sdkReadyListener);
  }

  // ✅ P0-3: Cleanup method for SDK ready listener
  override cleanup(): void {
    if (this.sdkReadyListener) {
      window.removeEventListener('message', this.sdkReadyListener);
      this.sdkReadyListener = null;
    }
    // Also remove language change listener
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
    i18n?.off?.('languageChanged', this._onLangChange as any);
  }



  // 공통 스크립트 실행 함수 (postMessage 전용)
  private async executeScriptInIframe(script: string): Promise<void> {
    const iframe = document.querySelector(
      'iframe[title="engine iframe"]',
    ) as HTMLIFrameElement;

    if (iframe?.contentWindow) {
      try {
        // postMessage 방식으로만 통신
        const waitForResponse = new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            logger.error('Script execution timeout after 20 seconds');
            reject(new Error('Script execution timeout'));
          }, 20000); // 20초 타임아웃

          // 응답 메시지 리스너
          const handleResponse = (event: MessageEvent) => {
            // 보안을 위해 origin 확인
            if (event.origin !== HCELL_ENGINE_ORIGIN) {
              return;
            }
            
            logger.info('Received response message:', event.data);
            if (isMessageType(event.data, 'SCRIPT_EXECUTED')) {
              clearTimeout(timeout);
              window.removeEventListener('message', handleResponse);
              if (event.data.success) {
                logger.info('Script executed successfully');
                resolve();
              } else {
                logger.error('Script execution failed:', event.data.error);
                reject(new Error(event.data.error ?? 'Script execution failed'));
              }
            }
          };

          window.addEventListener('message', handleResponse);
        });

        // postMessage를 통해 iframe에 스크립트 실행 요청
        const message: HcellExecuteScriptMessage = {
          type: 'EXECUTE_SCRIPT',
          script: script,
        };
        iframe.contentWindow.postMessage(message, HCELL_ENGINE_ORIGIN);

        logger.info('HcellService: Script execution request sent to local iframe');

        // 응답 대기
        await waitForResponse;
        logger.info('HcellService: Script execution completed');
      } catch (error) {
        logger.error('HcellService: local iframe communication error:', error);
        // ✅ P1-3 FIX: Use i18n.t() for reactive translations
        throw new Error(i18n.t('hcell:errors.localServiceCommunicationFailed'));
      }
    } else {
      // ✅ P1-3 FIX: Use i18n.t() for reactive translations
      throw new Error(i18n.t('hcell:errors.localServiceIframeNotFound'));
    }
  }

  // ✅ P1-1: 기능 실행 with proper type
  // ✅ REFACTORED: YAML 기반 제네릭 함수로 변경
  async executeFeature(featureId: string, _params: Record<string, unknown> = {}): Promise<void> {
    try {
      logger.info(`HcellService: Starting feature execution in local service - ${featureId}`);

      // SDK 초기화 완료까지 대기
      await this.waitForSDKReady();

      // ✅ insertImage는 특별 처리 (메인 창에서 파일 선택)
      if (featureId === 'insertImage') {
        await this.insertImage();
        return;
      }

      // YAML에서 샘플 코드 로드
      const samples = this.getSamples();
      if (!samples) {
        throw new Error('Samples not loaded');
      }

      const sampleCode = samples.SDK_EXAMPLES?.[featureId]?.javascript;
      if (!sampleCode) {
        throw new Error(`Sample code not found for ${featureId}`);
      }

      // 통합 스크립트 생성 및 실행
      const successMessage = i18n.t(`hcell:features.${featureId}.successMessage`);
      const errorMessage = i18n.t(`hcell:features.${featureId}.errorMessage`);
      const script = HcellScriptGenerator.buildExecutionScript(
        featureId,
        sampleCode,
        successMessage,
        errorMessage
      );
      await this.executeScriptInIframe(script);

      logger.info(`HcellService: Feature execution completed in local service - ${featureId}`);
    } catch (error) {
      // 정보 제공용 i18n 키들은 에러 로그를 출력하지 않음
      const INFO_I18N_KEYS = new Set([
        'hcell:errors.currentCellPosition',
        'hcell:errors.cellMoveCompleted',
        'hcell:errors.cellColorSet',
        'hcell:errors.cellBorderSet',
        'hcell:errors.rowInserted',
        'hcell:errors.rowDeleted',
        'hcell:errors.columnInserted',
        'hcell:errors.columnDeleted',
        'hcell:errors.cellsMerged',
        'hcell:errors.cellsUnmerged',
        'hcell:errors.imageInserted',
        'hcell:errors.shapeAdded',
      ]);

      const errObj = error && typeof error === 'object' ? error as Record<string, unknown> : null;
      const isInfoMessage = errObj &&
        Boolean(errObj.isI18nError) && INFO_I18N_KEYS.has(errObj.i18nKey as string);

      if (!isInfoMessage) {
        logger.error(`HcellService: Feature execution error (${featureId}):`, error);
      }
      throw error;
    }
  }


  // ✅ P1-1: Get samples with proper return type
  getSamples(): HcellSamplesConfig | null {
    return hcellSamplesMemoryCache;
  }

  // ✅ P1-1: Get sample for specific feature with proper return type
  getSample(featureId: string): HcellSample | null {
    if (!hcellSamplesMemoryCache) {
      return null;
    }
    return hcellSamplesMemoryCache.SDK_EXAMPLES?.[featureId] ?? null;
  }


  // SDK 초기화 완료까지 대기 (SdkIframeService.waitForReady에 위임)
  async waitForSDKReady(): Promise<void> {
    return this.waitForReady();
  }

  // ───────────────────────────────────────────────────────────
  // 특별 기능: insertImage (메인 창에서 파일 선택)
  // ───────────────────────────────────────────────────────────

  private async insertImage(): Promise<void> {
    // 여러 이미지 삽입 결과를 받기 위한 Promise (파일 개수만큼 결과를 수집)
    const insertedFiles: string[] = [];
    let expectedFileCount = 0;

    const imageInsertResultPromise = new Promise<{fileNames: string[], message: string}>((resolve, reject) => {
      const messageHandler = (event: MessageEvent) => {
        if (isMessageType(event.data, 'IMAGE_INSERT_RESULT')) {
          insertedFiles.push(event.data.fileName);

          // 모든 파일이 처리되었는지 확인
          if (insertedFiles.length >= expectedFileCount) {
            window.removeEventListener('message', messageHandler);
            resolve({
              fileNames: insertedFiles,
              message: `이미지 삽입 완료: ${insertedFiles.length}개의 파일이 성공적으로 삽입되었습니다. (${insertedFiles.join(', ')})`
            });
          }
        }
      };
      window.addEventListener('message', messageHandler);
      setTimeout(() => {
        window.removeEventListener('message', messageHandler);
        reject(new Error('Image insert request timeout'));
      }, 10000); // 여러 파일 처리를 위해 타임아웃 시간 증가
    });

    // 메인 창에서 파일 선택 처리
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*"; // 이미지 파일만 선택 가능
    input.multiple = true; // 여러 파일 선택 가능
    input.style.display = "none"; // 숨김 처리

    // DOM에 추가
    document.body.appendChild(input);

    return new Promise<void>((_resolve, reject) => {
      input.onchange = async (event) => {
        try {
          const files = (event.target as HTMLInputElement).files;
          if (!files || files.length === 0) {
            this.safeRemoveInput(input);
            reject(new Error('No files selected'));
            return;
          }

          logger.info('Selected files count:', files.length);
          expectedFileCount = files.length; // 예상 파일 개수 설정

          // 각 파일을 처리
          for (let index = 0; index < files.length; index++) {
            const file = files.item(index);
            if (!file) {
              logger.warn(`No file at index ${index}`);
              continue;
            }
            logger.info(`Processing file ${index + 1}: ${file.name} (${file.type}, ${file.size} bytes)`);

            // 이미지 크기 정보 추출
            const dimensions = await this.getImageDimensions(file);
            logger.info(`Image dimensions: ${dimensions.width}x${dimensions.height}`);

            // 파일을 ArrayBuffer로 읽기
            const arrayBuffer = await this.readFileAsArrayBuffer(file);

            // iframe에 파일 업로드 및 이미지 삽입 스크립트 실행 (크기 정보 포함)
            await this.uploadFileAndInsertImage(file.name, arrayBuffer, index, dimensions);
          }

          // input 요소 안전하게 제거
          this.safeRemoveInput(input);

          // 이미지 삽입 결과를 받아서 i18n 메시지로 표시
          const imageInsertResult = await imageInsertResultPromise;
          logger.info('Image insertion completed successfully:', imageInsertResult);

          // 성공 메시지를 i18n 에러로 표시 (정보 메시지로 처리됨)
          throw this.createI18nError('imageInserted', {
            fileName: imageInsertResult.fileNames.join(', '),
            message: imageInsertResult.message
          });

        } catch (error) {
          logger.error('Error processing files:', error);
          this.safeRemoveInput(input);
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      };

      input.oncancel = () => {
        logger.info('File selection cancelled');
        this.safeRemoveInput(input);
        reject(new Error('File selection cancelled'));
      };

      logger.info('Triggering file dialog...');
      // 파일 대화상자 열기
      input.click();
    });
  }

  // ───────────────────────────────────────────────────────────
  // 헬퍼 메서드들
  // ───────────────────────────────────────────────────────────

  private safeRemoveInput(input: HTMLInputElement): void {
    try {
      if (input?.parentNode) input.parentNode.removeChild(input);
    } catch (error) {
      logger.warn('Failed to remove input element:', error);
    }
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(new Error('File reading error'));
      reader.readAsArrayBuffer(file);
    });
  }

  private getImageDimensions(file: File): Promise<{width: number, height: number}> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      img.onerror = () => reject(new Error('Image loading error'));
      img.src = URL.createObjectURL(file);
    });
  }

  private async uploadFileAndInsertImage(fileName: string, arrayBuffer: ArrayBuffer, index: number, dimensions: {width: number, height: number}): Promise<void> {
    // 스크립트 생성기를 사용하여 이미지 업로드 스크립트 생성
    const script = HcellScriptGenerator.generateImageUploadScript(
      fileName,
      arrayBuffer,
      index,
      dimensions
    );

    // 공통 스크립트 실행 함수 사용
    await this.executeScriptInIframe(script);
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
export const hcellService = new HcellService();
export default hcellService;
